import React from 'react';
import { Card, Row, Col, Statistic, Progress, Timeline } from 'antd';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar
} from 'recharts';
import { 
  UserOutlined, 
  CalendarOutlined, 
  TrophyOutlined,
  ThunderboltOutlined,
  TeamOutlined
} from '@ant-design/icons';

const AdvancedAnalytics = ({ analyticsData }) => {
  if (!analyticsData) return null;

  const { 
    patientStats, 
    screeningStats, 
    eventStats, 
    riskAnalysis, 
    volunteerData 
  } = analyticsData;

  // Age distribution chart data
  const ageData = Object.entries(patientStats?.byAge || {}).map(([age, count]) => ({
    age,
    count,
    percentage: ((count / patientStats?.total || 1) * 100).toFixed(1)
  }));

  // Gender distribution
  const genderData = Object.entries(patientStats?.byGender || {}).map(([gender, count]) => ({
    name: gender.charAt(0).toUpperCase() + gender.slice(1),
    value: count,
    percentage: ((count / patientStats?.total || 1) * 100).toFixed(1)
  }));

  // Screening types performance
  const screeningTypes = [
    { name: 'Blood Pressure', value: screeningStats?.bloodPressure || 0, color: '#ff4d4f' },
    { name: 'Cholesterol', value: screeningStats?.cholesterol || 0, color: '#faad14' },
    { name: 'Glucose', value: screeningStats?.glucose || 0, color: '#1890ff' },
    { name: 'BMI', value: screeningStats?.bmi || 0, color: '#52c41a' },
    { name: 'Heart Rate', value: screeningStats?.heartRate || 0, color: '#f759ab' },
    { name: 'LPA', value: screeningStats?.lpa || 0, color: '#13c2c2' }
  ];

  // Volunteer performance radar
  const volunteerPerformance = [
    { subject: 'Screening Speed', A: 85, fullMark: 100 },
    { subject: 'Accuracy', A: 92, fullMark: 100 },
    { subject: 'Patient Satisfaction', A: 96, fullMark: 100 },
    { subject: 'Attendance', A: 88, fullMark: 100 },
    { subject: 'Training Score', A: 94, fullMark: 100 },
    { subject: 'Communication', A: 91, fullMark: 100 }
  ];

  // Event timeline data
  const upcomingEvents = eventStats?.upcomingEvents || [];

  return (
    <div>
      {/* Patient Demographics */}
      <Row gutter={24} style={{ marginBottom: 24 }}>
        <Col xs={24} lg={12}>
          <Card title="Patient Age Distribution" bordered={false}>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={ageData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="age" />
                <YAxis />
                <Tooltip formatter={(value, name) => [value, name === 'count' ? 'Patients' : name]} />
                <Bar dataKey="count" fill="#1890ff" />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="Gender Distribution" bordered={false}>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={genderData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percentage }) => `${name} ${percentage}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {genderData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={index === 0 ? '#1890ff' : '#f759ab'} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        </Col>
      </Row>

      {/* Screening Performance */}
      <Row gutter={24} style={{ marginBottom: 24 }}>
        <Col xs={24} lg={16}>
          <Card title="Screening Types Performance" bordered={false}>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={screeningTypes} layout="horizontal">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis dataKey="name" type="category" width={100} />
                <Tooltip />
                <Bar dataKey="value" fill="#52c41a" />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card title="System Performance" bordered={false}>
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <Statistic
                  title="Avg Processing Time"
                  value={screeningStats?.avgProcessingTime || 0}
                  suffix="min"
                  prefix={<ThunderboltOutlined />}
                  valueStyle={{ color: '#3f8600' }}
                />
              </Col>
              <Col span={24}>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                    <span>Efficiency Score</span>
                    <span>87%</span>
                  </div>
                  <Progress percent={87} status="active" />
                </div>
              </Col>
              <Col span={24}>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                    <span>Quality Score</span>
                    <span>94%</span>
                  </div>
                  <Progress percent={94} status="active" strokeColor="#52c41a" />
                </div>
              </Col>
              <Col span={24}>
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                    <span>Patient Satisfaction</span>
                    <span>96%</span>
                  </div>
                  <Progress percent={96} status="active" strokeColor="#1890ff" />
                </div>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      {/* Volunteer Performance & Events */}
      <Row gutter={24} style={{ marginBottom: 24 }}>
        <Col xs={24} lg={12}>
          <Card title="Volunteer Performance Metrics" bordered={false}>
            <ResponsiveContainer width="100%" height={300}>
              <RadarChart data={volunteerPerformance}>
                <PolarGrid />
                <PolarAngleAxis dataKey="subject" />
                <PolarRadiusAxis angle={90} domain={[0, 100]} />
                <Radar
                  name="Performance"
                  dataKey="A"
                  stroke="#1890ff"
                  fill="#1890ff"
                  fillOpacity={0.3}
                />
                <Tooltip />
              </RadarChart>
            </ResponsiveContainer>
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="Upcoming Events" bordered={false}>
            <Timeline
              items={upcomingEvents.map((event, index) => ({
                key: index,
                children: (
                  <div>
                    <div style={{ fontWeight: 'bold', marginBottom: 4 }}>
                      {event.title}
                    </div>
                    <div style={{ fontSize: '12px', color: '#666', marginBottom: 2 }}>
                      📅 {event.date}
                    </div>
                    <div style={{ fontSize: '12px', color: '#666', marginBottom: 2 }}>
                      📍 {event.location}
                    </div>
                    <div style={{ fontSize: '12px', color: '#666' }}>
                      👥 Expected: {event.expectedAttendees}
                    </div>
                  </div>
                ),
                color: index === 0 ? '#52c41a' : '#1890ff'
              }))}
            />
          </Card>
        </Col>
      </Row>

      {/* Risk Analysis Deep Dive */}
      <Row gutter={24} style={{ marginBottom: 24 }}>
        <Col xs={24}>
          <Card title="Risk Analysis Deep Dive" bordered={false}>
            <Row gutter={24}>
              <Col xs={24} lg={8}>
                <div style={{ textAlign: 'center', marginBottom: 20 }}>
                  <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#ff4d4f' }}>
                    {riskAnalysis?.followUpRequired || 0}
                  </div>
                  <div style={{ color: '#666' }}>Patients Need Follow-up</div>
                </div>
              </Col>
              <Col xs={24} lg={16}>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={riskAnalysis?.riskFactors || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="factor" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="count" fill="#ff7875" />
                  </BarChart>
                </ResponsiveContainer>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      {/* Key Metrics Summary */}
      <Row gutter={24}>
        <Col xs={24} sm={8} lg={6}>
          <Card>
            <Statistic
              title="Total Volunteers"
              value={volunteerData?.total || 0}
              prefix={<TeamOutlined style={{ color: '#1890ff' }} />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8} lg={6}>
          <Card>
            <Statistic
              title="Active Volunteers"
              value={volunteerData?.active || 0}
              prefix={<UserOutlined style={{ color: '#52c41a' }} />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8} lg={6}>
          <Card>
            <Statistic
              title="Avg Rating"
              value={volunteerData?.performance?.avgRating || 0}
              precision={1}
              prefix={<TrophyOutlined style={{ color: '#faad14' }} />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8} lg={6}>
          <Card>
            <Statistic
              title="Total Hours"
              value={volunteerData?.performance?.totalHours || 0}
              prefix={<CalendarOutlined style={{ color: '#722ed1' }} />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default AdvancedAnalytics;
